/**
 * An AI call campaign's call flow map (ring R8): how its calls go, drawn, and
 * whether the AI can follow what is drawn.
 *
 * The map is the CRM's ordinary flow map (owner "aiCampaign"): it loads,
 * autosaves, keeps its versions and meets a concurrent save the way a guide's
 * map does. On top of that this card
 *  - offers the three ready-made maps while the campaign has none;
 *  - lets an end node pick what the call came to (the widget's ref choice);
 *  - asks the server, a moment after every change, whether the AI could follow
 *    the map, marks the steps it could not on the canvas and lists why
 *    (`FlowCheckPanel`). Activating the campaign over a broken map is refused
 *    by the server, which reads the saved map; the page words that refusal;
 *  - opens "Proovi vestlust" (`../rehearse`) over the map on screen, once the
 *    check says the AI can follow it (or that nothing is drawn).
 *
 * The opening is not on the map and cannot be: code says it (the AI and
 * recording disclosure), so the start node only shows where it sits.
 *
 * An alert campaign (ring R9) needs an end whose outcome is the defect report;
 * while the map has none, the card says so above the check. That warning
 * follows the angle on screen, as it is about what saving needs; the rehearsal
 * follows the saved angle, as the server runs it on the stored campaign.
 */
import { useMemo, useState } from "react";
import { MessagesSquare } from "lucide-react";
import type { FlowRefChoices } from "@wa/widgets/flow-map/model";
import { useT } from "../../../../../i18n/I18nProvider";
import { FLOW_OUTCOMES, flowBlocksActivation, flowMissesReportEnd, problemMarks } from "../../../../../lib/aiCallFlow";
import { Btn } from "../../../../ui";
import { Note } from "../../../settings/panel/fields";
import { FlowMapCard, useFlowMapDoc } from "../../../_shared/flowmap";
import { RehearseDialog } from "../rehearse";
import { FlowCheckPanel } from "./FlowCheckPanel";
import { FlowTemplatePicker } from "./FlowTemplatePicker";
import { currentCheck, useFlowCheck } from "./useFlowCheck";

export function CampaignFlowCard({
  campaignId,
  canManage,
  active,
  alert = false,
  rehearseAlert = false,
}: {
  campaignId: string;
  canManage: boolean;
  /** The campaign is active: a broken map then means no calls, and the panel says so. */
  active: boolean;
  /** The campaign's angle on screen is an alert. */
  alert?: boolean;
  /** The saved campaign is an alert: the rehearsal the server runs is one. */
  rehearseAlert?: boolean;
}) {
  const t = useT();
  const map = useFlowMapDoc("aiCampaign", campaignId);
  const ready = map.phase === "ready";
  const result = useFlowCheck(ready ? map.doc : null, canManage);
  const check = currentCheck(result);
  const [rehearsing, setRehearsing] = useState(false);
  // Only over a map the AI can follow, and only once the check has answered for it.
  const canRehearse = canManage && result.state === "ready" && !flowBlocksActivation(result.check);

  const marks = useMemo(() => (check ? problemMarks(check.problems, map.doc) : undefined), [check, map.doc]);
  const refChoices = useMemo<FlowRefChoices>(
    () => ({
      type: "outcome",
      kinds: ["end"],
      label: t("aiCalls.flow.outcome"),
      noneLabel: t("aiCalls.flow.outcomeNone"),
      options: FLOW_OUTCOMES.map((id) => ({ id, label: t(`aiCalls.flow.outcomes.${id}`) })),
    }),
    [t],
  );

  const footer = canManage ? (
    <div className="space-y-3">
      {alert && flowMissesReportEnd(check) ? <Note tone="warn">{t("aiCalls.flow.alertNoReport")}</Note> : null}
      <FlowCheckPanel result={result} doc={map.doc} active={active} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="crm-sub text-xs">{t("aiCalls.rehearse.openHint")}</p>
        <Btn onClick={() => setRehearsing(true)} disabled={!canRehearse}>
          <MessagesSquare className="size-3.5" />
          {t("aiCalls.rehearse.open")}
        </Btn>
      </div>
    </div>
  ) : null;

  return (
    <>
      <FlowMapCard
        owner="aiCampaign"
        ownerId={campaignId}
        map={map}
        canEdit={canManage}
        title={t("aiCalls.flow.title")}
        ariaLabel={t("aiCalls.flow.title")}
        height="clamp(420px, calc(100vh - 380px), 760px)"
        emptyState={(start) => <FlowTemplatePicker canEdit={canManage} start={start} />}
        nodeMarks={marks}
        refChoices={refChoices}
        footer={footer}
      />
      {rehearsing ? <RehearseDialog campaignId={campaignId} doc={map.doc} alert={rehearseAlert} onClose={() => setRehearsing(false)} /> : null}
    </>
  );
}
